import { type FC, useCallback, useMemo } from 'react'
import { Button, Flex, VStack } from '@chakra-ui/react'
import { type AddToCartPayload, type ICartItemDetailSubItem, RelatedProductTypes } from 'ecosystem'
import { overrideText } from 'ui/lib/overrides'
import { isPriceOnRequest } from 'shared-utils'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import ProductDetailPrice from '../../ProductDetailPrice'
import VariantPicker from '../../VariantPicker/VariantPicker'
import ProductBundles from '../../ProductBundles'
import ProductSubProducts from '../../ProductSubProducts'
import ProductDetailAddToCart from '../../ProductDetailAddToCart'
import { useProductContext } from '../../../hooks/useProductContext'
import type { RegularProductInformationOverrides } from './types'
import useRegularVariants from './useRegularVariants'

const RegularProductPurchaseData: FC = () => {
  const overrides = useOverridesContext<keyof RegularProductInformationOverrides>()
  const {
    product,
    onAddToCart,
    relatedGroups,
    bundles,
    onProductRequest,
    deliveryRenderer,
    variants
  } = useProductContext()
  const hasPriceRequest = isPriceOnRequest(product)
  const { selectVariant, selectVariantOnUpdate, isVariantSelected, prices } =
    useRegularVariants(product)

  const subProducts = useMemo(() => relatedGroups[RelatedProductTypes.CUSTOM] || [], [])

  const handleAddRegularProductToCart = useCallback(
    (payload: AddToCartPayload) => {
      const subItems: ICartItemDetailSubItem[] = bundles
        ? bundles.map((bundleItem) => ({
            product: bundleItem.product,
            originalQty: bundleItem.quantity,
            qty: bundleItem.quantity * payload.qty
          }))
        : []

      onAddToCart({
        id: payload.product.id,
        type: 'SINGLE',
        qty: payload.qty,
        meta: {
          singleType: {
            product: payload.product,
            subItems
          }
        }
      })
    },
    [onAddToCart, bundles]
  )

  return (
    <>
      {!hasPriceRequest ? <ProductDetailPrice {...prices} /> : null}

      {(!!variants || !!bundles || !!subProducts?.length) && (
        <VStack w="100%" className="wrapper-variant-picker-bundle-subproducts">
          <VariantPicker
            onSelectVariant={selectVariant}
            onVariantsUpdate={selectVariantOnUpdate}
            variantGroupId={product.variantGroup}
            {...{ isVariantSelected }}
          />
          <ProductBundles />
          <ProductSubProducts subProducts={relatedGroups[RelatedProductTypes.CUSTOM] || []} />
        </VStack>
      )}

      {hasPriceRequest ? (
        <Flex justify="center" w="full">
          <Button
            aria-label={overrideText('Offertförfrågan', overrides?.productRequest)}
            fontSize="md"
            onClick={() => {
              onProductRequest(product)
            }}
            size="lg"
            w="100%"
          >
            {overrideText('Offertförfrågan', overrides?.productRequest)}
          </Button>
        </Flex>
      ) : (
        <ProductDetailAddToCart
          onAddToCart={handleAddRegularProductToCart}
          totalPrices={prices}
          key={product.id}
        />
      )}
      {deliveryRenderer()}
    </>
  )
}

export default RegularProductPurchaseData
